import {FieldNames, normalizeFieldName} from '@hconnect/tuc-processes/fields'
import {
  useSlotValidationMutation,
  SLOT_MANAGEMENT_DEPENDENT_FIELDS
} from '@hconnect/tuc-processes/slotManagement'
import {useMemo, useCallback} from 'react'

import {validateSlot} from '../../../../shared/order-editor/components/fields/Time/api/validateSlot'
import {FIELD_ID_TO_FIELD_NAMES} from '../../../utils/fieldIdToFieldNames'

import {FieldEditorViewProps, FieldEditorView} from './FieldEditorView'
import {useOrderProcessFormContext} from '@hconnect/tuc-processes/form/useOrderProcessFormContext'

export interface SlotManagementFieldEditorViewProps extends FieldEditorViewProps {
  goToField?: (fieldId: string, source?: string) => void
  navigationSource?: string
  onSlotValidationFailure?: (messageKey: string) => void
}

export const SlotManagementFieldEditorView = (props: SlotManagementFieldEditorViewProps) => {
  const {
    onSelectAndContinue: baseOnSelectAndContinue,
    onBack: baseOnBack,
    onSlotValidationFailure,
    ...rest
  } = props
  const { shouldValidate, mutation } = useSlotValidationMutation({
    slotValidationMutationFn: validateSlot
  })
  const { resetField } = useOrderProcessFormContext()

  // Field names backing the currently open field editor (used for slot-field detection and discard/reset)
  const normalizedFieldNames = useMemo(() => {
    const fieldNameMapping = FIELD_ID_TO_FIELD_NAMES[props.fieldId]
    if (!fieldNameMapping) {
      return []
    }

    const names = Array.isArray(fieldNameMapping)
      ? (fieldNameMapping as FieldNames[])
      : [fieldNameMapping as FieldNames]

    return names.map(normalizeFieldName).map((f) => f.field)
  }, [props.fieldId])

  const isSlotField = useMemo(
    () =>
      SLOT_MANAGEMENT_DEPENDENT_FIELDS.some((fieldName) => normalizedFieldNames.includes(fieldName)),
    [normalizedFieldNames]
  )

  // Discards unsaved edits for the current field so navigating back never persists a pending change
  const resetCurrentFieldValues = useCallback(() => {
    normalizedFieldNames.forEach((fieldName) => resetField(fieldName))
  }, [normalizedFieldNames, resetField])

  const onSelectAndContinue = useCallback(async () => {
    // After Date step always navigate to Time step
    if (props.fieldId === 'date') {
      props.goToField?.('time', 'date')
      return
    }

    if (isSlotField && shouldValidate) {
      // A failed validation request never blocks the change - it is submitted for dispatch approval instead
      try {
        await mutation.mutateAsync()
      } catch {
        onSlotValidationFailure?.('orderIntake.rmc.orderManagement.slotValidation.requestFailed')
      }
    }
    baseOnSelectAndContinue?.()
  }, [
    props,
    isSlotField,
    shouldValidate,
    baseOnSelectAndContinue,
    onSlotValidationFailure,
    mutation
  ])

  const onBack = useCallback(() => {
    // Discard unsaved edits so going back never keeps a pending field change
    resetCurrentFieldValues()

    // Smart back navigation for Time field
    if (props.fieldId === 'time') {
      // If we came from date field, go back to date field
      if (props.navigationSource === 'date') {
        props.goToField?.('date')
        return
      }
      // Otherwise, go back to overview (or wherever we came from)
      baseOnBack()
      return
    }
    baseOnBack()
  }, [props, baseOnBack, resetCurrentFieldValues])

  return (
    <FieldEditorView
      onSelectAndContinue={onSelectAndContinue}
      isLoading={mutation.isLoading}
      onBack={onBack}
      {...rest}
    />
  )
}
